// Shared "journey" selection made on the presentation screen and consumed both
// there and deep inside the configurator (ConfigPanel, SpecDetails). Generic
// across any model that defines models.<Model>.journeys (Corvette's
// Stingray/GS X, Hummer's SUV/Pickup) — module-level + useSyncExternalStore
// for reactivity, same rationale as viewportLoaderText.ts: the value must cross
// the presentation⇄configurator boundary without prop-drilling through
// App → ConfigPanel.
import { useSyncExternalStore } from 'react';
import { uimetadata } from './metadata';

export type JourneyKey = string;

/** All journey definitions from the active model's own ui-metadata. */
export const getJourneys = (): any[] => {
  const modelId = (uimetadata as any).config?.model;
  return (uimetadata as any).models?.[modelId]?.journeys ?? [];
};

// Defaults to the active model's first journey (its own [0] entry is treated
// as the "default" one, same as isDefault:true is for variants) rather than a
// hardcoded key - models with no journeys resolve this to '', which every
// consumer below already treats as "no journey selected".
let _journey: JourneyKey = getJourneys()[0]?.key ?? '';
const listeners = new Set<() => void>();

export const getSelectedJourney = (): JourneyKey => _journey;

export const setSelectedJourney = (j: JourneyKey): void => {
  if (j !== _journey) {
    _journey = j;
    listeners.forEach((l) => l());
  }
};

const subscribe = (cb: () => void): (() => void) => {
  listeners.add(cb);
  return () => {
    listeners.delete(cb);
  };
};

/** Reactive hook — re-renders on journey change. */
export const useSelectedJourney = (): JourneyKey =>
  useSyncExternalStore(subscribe, getSelectedJourney, getSelectedJourney);

/**
 * Which journey a variant belongs to, via each journey's own explicit
 * `variantIds` membership list. A variant that isn't listed under any journey
 * (a model with a single journey that doesn't bother enumerating every one of
 * its own variants, or one that predates journeys being added) falls back to
 * the first journey, so filtering by `journeyOf(id) === selectedJourney`
 * still includes it rather than silently hiding it.
 */
export const journeyOf = (variantId: string): JourneyKey => {
  const journeys = getJourneys();
  const match = journeys.find((j: any) => j.variantIds?.includes(variantId));
  return match?.key ?? journeys[0]?.key ?? '';
};

export const getJourney = (key: JourneyKey): any =>
  getJourneys().find((j: any) => j.key === key);

/**
 * Specifications for a journey: the active model's own `specifications` block
 * merged with the journey's own overrides. A journey that doesn't override a
 * given field falls back to the model-level block.
 */
const getBaseSpecs = (): any => {
  const modelId = (uimetadata as any).config?.model;
  return (uimetadata as any).models?.[modelId]?.specifications;
};

export const getJourneySpecs = (key: JourneyKey): any => {
  const base = getBaseSpecs();
  const own = getJourney(key)?.specifications;
  return own ? { ...base, ...own } : base;
};
